import React, { useState, useEffect } from 'react';
import { MenuItem, TextField, Box, Menu, InputAdornment, Select, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { useToast } from 'use-toast-mui';
import { useTranslation } from 'react-i18next';

import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputPrice } from '../../../../form-components/FormInputPrice';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import BranchSelectModal from '../../../common/modal/BranchSelectModal';
import AccessDoorSelect from '../../common/AccessDoorSelect';

import {
  CardsContainer,
  BasicInfoColumn,
  InfoCard,
  InfoCardTitle,
  InfoCardHeader,
  FieldGroup,
  FieldLabel,
  FieldLabelRow,
  FieldLabelWithInfo,
  InfoIconSmall,
  InputUnit,
  DiscountUnitSelect,
  HalfWidthContainer,
  HalfWidthField,
  RadioGroupRow,
  RadioOption,
  RadioCircleNew,
  RadioDotNew,
  RadioLabelNew,
  ToggleContainer,
  ToggleLabel,
  StyledSwitch,
  TimeTilde,
  DatePickerRow,
  DatePickerField,
  CheckboxRow,
  CheckboxNew,
  CheckboxLabelNew,
  BottomActionsNew,
  CancelButtonNew,
  NextButtonNew,
  ErrorFieldWrapper,
  SportTagPlaceholder,
  UsagePeriodInputBox,
  UsageInputRow,
  UsageInputColumn,
  UsageCountInputBox,
  RequiredMark,
} from './TrialEditStyled';
import SportTypeSelect from '../../../common/SportTypeSelect';

// 이용기간 최대 자릿수 — 9자리면 Java Integer(2,147,483,647) 범위를 넘지 않는다
const USAGE_PERIOD_MAX_LENGTH = 9;

// 상품 유형 (Figma: 이용권/개인레슨/그룹레슨 → 기존 classType 매핑)
// 2열 배치 순서: 개인·그룹 / 이용권·OT
const PRODUCT_TYPES = [
  { label: '개인레슨', value: 'PRIVATE_LESSON' },
  { label: '그룹레슨', value: 'GROUP_LESSON' },
  { label: '이용권', value: 'PASS' },
  { label: 'OT', value: 'OT' },
];

// 상품 유형 라디오 2열 배치, 열 기준으로 정렬
const TYPE_RADIO_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 120px)',
  columnGap: '24px',
  rowGap: '12px',
};

// 체크 아이콘
const CheckIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
    <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// 인포 아이콘
const InfoCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
    <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
      ?
    </text>
  </svg>
);

function TrialEditStep01({
  control,
  watch,
  setValue,
  user,
  usagePeriodType,
  setUsagePeriodType,
  discountType,
  setDiscountType,
  hasDiscountEndDate,
  setHasDiscountEndDate,
  handleRegularPriceChange,
  handleDiscountRateChange,
  handleFinalPriceChange,
  onNext,
  onCancel,
  loader,
  centrerInfo,
  exposureYn,
  onExposureToggle,
  isEditMode,
  copySourceName = '',
  branchOptions = [],
  legacyBranchNames = {},
  selectedDoorIds,
  onDoorIdsChange,
  onDoorConvertedChange,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [errorFields, setErrorFields] = useState({});

  // 판매·이용지점 선택 모달 상태 (#73 판매/이용 분리 — 지점이 많아도 검색·전체선택 가능한 모달)
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [branchMenuField, setBranchMenuField] = useState(null); // 'saleBranchIds' | 'useBranchIds'
  const [sameAsSale, setSameAsSale] = useState(false);
  // 이용지점=구매한 지점(소속지점) 모드(#98) — 켜면 이용지점 목록 대신 회원이 결제한 지점 1곳만 이용
  const useSaleBranch = watch('useSaleBranchYn') === 'Y';
  const useBranchLocked = sameAsSale || useSaleBranch;
  const toggleUseSaleBranch = () => {
    const next = !useSaleBranch;
    setValue('useSaleBranchYn', next ? 'Y' : 'N');
    if (next) {
      setSameAsSale(false);
      setValue('useBranchIds', []);
    } else if (!(watch('useBranchIds') || []).length) {
      setValue('useBranchIds', watch('saleBranchIds') || []);
    }
  };
  const toggleSameAsSale = () => {
    if (!sameAsSale) setValue('useSaleBranchYn', 'N');
    setSameAsSale(!sameAsSale);
  };
  const myBranchIsDirect = branchOptions.some(option => option.branchId === user?.branch?.branchId);
  const isMultiBranch = branchOptions.length > 1 && myBranchIsDirect;
  const branchNameOf = branchId =>
    branchOptions.find(option => option.branchId === branchId)?.branchName
      ?? legacyBranchNames[branchId]
      ?? (branchId === user?.branch?.branchId ? user?.branch?.branchName : `지점 #${branchId}`);

  const branchFieldValue = field => (field ? watch(field) : []) || [];

  // 선택 요약 — 1개면 지점명 그대로, 여러 개면 "OO 외 N개"
  const branchSummaryText = field => {
    const ids = branchFieldValue(field);
    if (ids.length === 0) return null;
    if (ids.length === 1) return branchNameOf(ids[0]);
    return `${branchNameOf(ids[0])} 외 ${ids.length - 1}개`;
  };

  const openBranchModal = field => {
    setBranchMenuField(field);
    setBranchModalOpen(true);
  };

  // 이용지점 "판매지점과 동일" — 체크되어 있는 동안 판매지점 변경을 그대로 따라간다
  const sameAsSaleSourceKey = JSON.stringify(watch('saleBranchIds') || []);
  useEffect(() => {
    if (sameAsSale) {
      setValue('useBranchIds', JSON.parse(sameAsSaleSourceKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsSale, sameAsSaleSourceKey]);

  // 할인 단위 드롭다운 메뉴 상태
  const [discountUnitAnchor, setDiscountUnitAnchor] = useState(null);

  // 운동종목 옵션
  const sportOptions =
    centrerInfo?.branchDetailInfo?.sportTypes?.map(type => ({
      label: t(type),
      value: type,
    })) || [];

  // 선택된 운동종목 (배열로 관리, 백엔드 단일값과 호환)
  const sportsValue = watch('sports');
  const selectedSports = sportsValue ? (Array.isArray(sportsValue) ? sportsValue : [sportsValue]) : [];

  // 운동종목 선택 변경 — 빈 배열은 기존 규약대로 '' 로 저장한다
  const handleSportsChange = nextSports => {
    setValue('sports', nextSports.length > 0 ? nextSports : '');
  };

  const productType = watch('classType');
  const appDisplayYn = watch('appDisplayYn');
  const isPass = productType === 'PASS';
  const isLesson = productType === 'PRIVATE_LESSON' || productType === 'GROUP_LESSON' || productType === 'OT';

  // 필드 값 변경 시 해당 에러 제거
  const watchedFields = watch([
    'sports',
    'trialName',
    'classType',
    'usagePeriod',
    'regularPrice',
    'usageCount',
    'classDefaultMinute',
  ]);
  useEffect(() => {
    const [sports, trialName, classType, usagePeriod, regularPrice, usageCount, classDefaultMinute] = watchedFields;
    setErrorFields(prev => {
      const updated = { ...prev };
      const hasSports = Array.isArray(sports) ? sports.length > 0 : !!sports;
      if (hasSports) delete updated.sports;
      if (trialName) delete updated.trialName;
      if (classType) delete updated.classType;
      if (usagePeriod) delete updated.usagePeriod;
      if (regularPrice) delete updated.regularPrice;
      if (usageCount && Number(usageCount) > 0) delete updated.usageCount;
      if (classDefaultMinute && Number(classDefaultMinute) > 0) delete updated.classDefaultMinute;
      return updated;
    });
  }, [
    watchedFields[0],
    watchedFields[1],
    watchedFields[2],
    watchedFields[3],
    watchedFields[4],
    watchedFields[5],
    watchedFields[6],
  ]);

  // 유효성 검사 — 화면 인풋 순서대로 체크
  const validateForm = () => {
    const values = watch();
    const errorList = [];

    // Card 1: 기본정보
    if (!values.classType) errorList.push({ field: 'classType', msg: '상품유형을 선택해주세요' });
    if (!values.trialName) errorList.push({ field: 'trialName', msg: '일일권명을 입력해주세요' });
    const hasSports = Array.isArray(values.sports) ? values.sports.length > 0 : !!values.sports;
    if (!hasSports) errorList.push({ field: 'sports', msg: '운동종목을 선택해주세요' });

    // Card 2: 이용설정 — 이용권(PASS)은 이용설정 카드가 없음 (이용기간 1일 고정)
    if (isLesson && !values.usagePeriod) errorList.push({ field: 'usagePeriod', msg: '이용기간을 입력해주세요' });
    if (isLesson && (!values.usageCount || Number(values.usageCount) <= 0)) {
      errorList.push({ field: 'usageCount', msg: '이용 횟수를 입력해주세요' });
    }
    if (isLesson && (!values.classDefaultMinute || Number(values.classDefaultMinute) <= 0)) {
      errorList.push({ field: 'classDefaultMinute', msg: '수업 시간을 입력해주세요' });
    }
    // 중지권: 직접 입력 선택 시 값 필수
    if (isLesson && values.stopType === 'CUSTOM' && (!values.stopCount || Number(values.stopCount) <= 0)) {
      errorList.push({ field: 'stopCount', msg: '중지권 횟수를 입력해주세요' });
    }
    // 이용제한: 무제한 미체크 시 값 필수
    if (isLesson && !values.dailyUsageLimits && (!values.dailyUsageLimit || Number(values.dailyUsageLimit) <= 0)) {
      errorList.push({ field: 'dailyUsageLimit', msg: '일일 이용제한 횟수를 입력해주세요' });
    }
    if (isLesson && !values.weeklyUsageLimits && (!values.weeklyUsageLimit || Number(values.weeklyUsageLimit) <= 0)) {
      errorList.push({ field: 'weeklyUsageLimit', msg: '주간 이용제한 횟수를 입력해주세요' });
    }
    // 최대 예약권: 레슨 + 무제한 미체크 시 필수
    if (isLesson && !values.reservationCounts && (!values.reservationCount || Number(values.reservationCount) <= 0)) {
      errorList.push({ field: 'reservationCount', msg: '최대 예약권 횟수를 입력해주세요' });
    }
    // 예약 취소권: 레슨 + 무제한 미체크 시 필수 (0회 허용 — 예약 취소 불가 의미)
    if (isLesson && !values.cancelCounts && (values.cancelCount === '' || values.cancelCount === undefined || values.cancelCount === null)) {
      errorList.push({ field: 'cancelCount', msg: '예약 취소권 횟수를 입력해주세요' });
    }

    // Card 3: 금액설정
    if (!values.regularPrice) errorList.push({ field: 'regularPrice', msg: '판매가를 입력해주세요' });

    // Card 3: 구매설정 — 무제한 미체크 시 필수
    if (!values.totalQuantityUnlimited && (!values.totalQuantity || Number(values.totalQuantity) <= 0)) {
      errorList.push({ field: 'totalQuantity', msg: '총 수량을 입력해주세요' });
    }
    if (!values.maxTrialPerUserUnlimited && (!values.maxTrialPerUser || Number(values.maxTrialPerUser) <= 0)) {
      errorList.push({ field: 'maxTrialPerUser', msg: '인당 최대 구매 횟수를 입력해주세요' });
    }

    // Card 4: 기간설정 — 종료일 없음 미체크 시 종료일 필수
    if (!values.hasSaleEndDate && !values.saleEndDate) {
      errorList.push({ field: 'saleEndDate', msg: '판매 종료일을 선택해주세요' });
    }
    if (!hasDiscountEndDate && !values.discountEndDate) {
      errorList.push({ field: 'discountEndDate', msg: '할인 종료일을 선택해주세요' });
    }

    if (errorList.length > 0) {
      setErrorFields({ [errorList[0].field]: true });
      toast.error(t(errorList[0].msg));
      return false;
    }
    setErrorFields({});
    return true;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <>
      <CardsContainer>
        {/* 기본정보 + 출입설정을 한 칼럼으로 세로 정렬 */}
        <BasicInfoColumn>
          {/* Card 1: 기본정보 */}
          <InfoCard>
            <InfoCardHeader>
              <InfoCardTitle>기본정보</InfoCardTitle>
              {isEditMode && (
                <ToggleContainer>
                  <ToggleLabel>앱 노출</ToggleLabel>
                  <StyledSwitch
                    checked={exposureYn === 'Y'}
                    onChange={onExposureToggle}
                  />
                </ToggleContainer>
              )}
            </InfoCardHeader>

            {/* 상품 유형 */}
            <FieldGroup>
              <FieldLabel>상품 유형<RequiredMark>*</RequiredMark></FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.classType} fullWidth>
                <RadioGroupRow sx={{ ...TYPE_RADIO_GRID_SX, ...(isEditMode ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
                  {PRODUCT_TYPES.map(type => (
                    <RadioOption key={type.value} onClick={() => !isEditMode && setValue('classType', type.value)}>
                      <RadioCircleNew checked={productType === type.value}>
                        {productType === type.value && <RadioDotNew />}
                      </RadioCircleNew>
                      <RadioLabelNew>{type.label}</RadioLabelNew>
                    </RadioOption>
                  ))}
                </RadioGroupRow>
              </ErrorFieldWrapper>
            </FieldGroup>

            {/* 이용 기간 (이용권: 1일 고정, 수정 불가) */}
            {isPass && (
              <FieldGroup>
                <FieldLabelWithInfo>
                  <FieldLabel>이용 기간</FieldLabel>
                  <Tooltip
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>이용권 일일권은 이용 기간이 1일로 고정됩니다.</Box>
                    }
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <InfoCircleIcon />
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <FormInputText
                  control={control}
                  name="usagePeriodFixed"
                  staticValue="1"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>일</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </FieldGroup>
            )}

            {/* 일일권명 */}
            <FieldGroup>
              <FieldLabel>일일권명<RequiredMark>*</RequiredMark></FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.trialName} fullWidth>
                <FormInputText
                  control={control}
                  rules={{ required: true }}
                  name="trialName"
                  placeholder={copySourceName ? `${copySourceName} (원본 이름)` : '일일권명을 입력해 주세요.'}
                />
              </ErrorFieldWrapper>
            </FieldGroup>

              {/* 판매지점·이용지점 (#73 판매/이용 분리 — 직영점 2곳 이상일 때만 다중 선택, 모달에서 체크박스로 선택) */}
              {isMultiBranch ? (
                <>
                  <FieldGroup>
                    <FieldLabelWithInfo>
                      <FieldLabel>판매지점</FieldLabel>
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>이 상품을 판매(등록)할 수 있는 지점입니다. 매출은 실제 결제가 등록된 지점에 귀속됩니다.</Box>
                        }
                        arrow
                        placement="top">
                        <InfoIconSmall>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                            <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                              ?
                            </text>
                          </svg>
                        </InfoIconSmall>
                      </Tooltip>
                    </FieldLabelWithInfo>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 14px',
                          border: '1px solid #E0E0E0',
                          borderRadius: '8px',
                          backgroundColor: '#F7F7F7',
                          fontSize: '14px',
                          color: '#333',
                        }}>
                        {branchSummaryText('saleBranchIds') || (
                          <SportTagPlaceholder>이 상품을 판매할 지점을 선택해 주세요.</SportTagPlaceholder>
                        )}
                      </Box>
                      <Box
                        onClick={() => openBranchModal('saleBranchIds')}
                        sx={{
                          flexShrink: 0,
                          padding: '0 14px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '8px',
                          border: '1px solid #4AB3BC',
                          color: '#4AB3BC',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          '&:hover': { backgroundColor: '#EAF7F8' },
                        }}>
                        지점 선택
                      </Box>
                    </Box>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabelWithInfo>
                      <FieldLabel>이용지점</FieldLabel>
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>구매한 회원이 실제로 출입·예약 등 이용할 수 있는 지점입니다. 판매지점과 달라도 됩니다. '구매한 지점만 이용'을 켜면 지점 목록 대신 회원이 결제한 지점 1곳에서만 이용합니다.</Box>
                        }
                        arrow
                        placement="top">
                        <InfoIconSmall>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                            <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                              ?
                            </text>
                          </svg>
                        </InfoIconSmall>
                      </Tooltip>
                    </FieldLabelWithInfo>
                    <CheckboxRow sx={{ marginBottom: '8px' }}>
                      <CheckboxNew checked={sameAsSale} onClick={toggleSameAsSale}>
                        {sameAsSale && (
                          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </CheckboxNew>
                      <CheckboxLabelNew>판매지점과 동일</CheckboxLabelNew>
                      {/* 소속지점 체크(#98)는 '판매지점과 동일' 옆에 나란히 */}
                      <Box sx={{ width: '20px', flexShrink: 0 }} />
                      <CheckboxNew checked={useSaleBranch} onClick={toggleUseSaleBranch}>
                        {useSaleBranch && (
                          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </CheckboxNew>
                      <CheckboxLabelNew>구매한 지점만 이용 (소속지점)</CheckboxLabelNew>
                    </CheckboxRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: sameAsSale ? 0.55 : 1 }}>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 14px',
                          border: '1px solid #E0E0E0',
                          borderRadius: '8px',
                          backgroundColor: '#F7F7F7',
                          fontSize: '14px',
                          color: '#333',
                        }}>
                        {useSaleBranch
                          ? '구매한 지점 (회원이 결제한 지점에서만 이용)'
                          : branchSummaryText('useBranchIds') || (
                              <SportTagPlaceholder>구매 회원이 이용 가능한 지점을 선택해 주세요.</SportTagPlaceholder>
                            )}
                      </Box>
                      <Box
                        onClick={() => !useBranchLocked && openBranchModal('useBranchIds')}
                        sx={{
                          flexShrink: 0,
                          padding: '0 14px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '8px',
                          border: '1px solid #4AB3BC',
                          color: '#4AB3BC',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: useBranchLocked ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                          pointerEvents: useBranchLocked ? 'none' : 'auto',
                          '&:hover': { backgroundColor: '#EAF7F8' },
                        }}>
                        지점 선택
                      </Box>
                    </Box>
                  </FieldGroup>
                  <BranchSelectModal
                    open={branchModalOpen}
                    title={branchMenuField === 'saleBranchIds' ? '판매지점 선택' : '이용지점 선택'}
                    branches={branchOptions}
                    selectedIds={branchFieldValue(branchMenuField)}
                    onChangeIds={ids => setValue(branchMenuField, ids)}
                    onClose={() => setBranchModalOpen(false)}
                  />
                </>
              ) : (
                <FieldGroup>
                  <FieldLabel>판매지점</FieldLabel>
                  <FormInputDropdown
                    name="branchId"
                    control={control}
                    disabled
                    staticValue={user?.branch?.branchId}
                    options={[{ label: user?.branch?.branchName || '지점 선택', value: user?.branch?.branchId }]}
                    placeholder="판매지점을 선택해 주세요."
                  />
                </FieldGroup>
              )}

            {/* 운동종목 (multi-select 태그) */}
            <FieldGroup>
              <FieldLabel>운동종목<RequiredMark>*</RequiredMark></FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.sports} fullWidth>
                <SportTypeSelect
                  options={sportOptions}
                  value={selectedSports}
                  onChange={handleSportsChange}
                  hasError={errorFields.sports}
                />
              </ErrorFieldWrapper>
            </FieldGroup>
          </InfoCard>

          {/* Card: 출입설정 — 전환 지점에서만 노출. 미제한 문은 선택 고정 표시 */}
          <AccessDoorSelect
            branchId={user?.branch?.branchId}
            token={user?.token}
            selectedDoorIds={selectedDoorIds}
            onChange={onDoorIdsChange}
            onConvertedChange={onDoorConvertedChange}
            wrapper={content => (
              <InfoCard>
                <InfoCardTitle>출입설정</InfoCardTitle>
                {content}
              </InfoCard>
            )}
          />
        </BasicInfoColumn>

        {/* Card 2: 이용설정 — 레슨 유형만 노출. 이용권(PASS)은 기본정보의 이용기간 1일 고정으로 대체 */}
        {isLesson && (
        <InfoCard>
          <InfoCardTitle>이용설정</InfoCardTitle>

          {/* 이용기간 */}
          <FieldGroup>
            <UsageInputRow>
              <UsageInputColumn>
                <FieldLabelWithInfo>
                  <FieldLabel>이용 기간<RequiredMark>*</RequiredMark></FieldLabel>
                  <Tooltip
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>회원이 구매 후 이용할 수 있는 기간을 설정합니다.</Box>
                    }
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <InfoCircleIcon />
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <UsagePeriodInputBox hasError={errorFields.usagePeriod}>
                  <FormInputText
                    className="usage-period-input"
                    control={control}
                    rules={{ required: true }}
                    name="usagePeriod"
                    placeholder="0"
                    // BE usagePeriod 는 Integer — 숫자 외 문자·10자리 이상이면 변환 실패(400)라 입력 단계에서 막는다
                    inputProps={{ inputMode: 'numeric', maxLength: USAGE_PERIOD_MAX_LENGTH }}
                    onChange={e => setValue('usagePeriod', e.target.value.replace(/\D/g, ''))}
                  />
                  <Select
                    className="usage-period-select"
                    value={usagePeriodType ? 'MONTH' : 'DAY'}
                    onChange={e => setUsagePeriodType(e.target.value === 'MONTH')}
                    IconComponent={KeyboardArrowDownIcon}
                    variant="standard"
                    disableUnderline>
                    <MenuItem value="MONTH">개월</MenuItem>
                    <MenuItem value="DAY">일</MenuItem>
                  </Select>
                </UsagePeriodInputBox>
              </UsageInputColumn>
            </UsageInputRow>
          </FieldGroup>

          {/* 레슨일 때: 이용횟수 + 수업시간 */}
          {isLesson && (
            <FieldGroup>
              <UsageInputRow>
                <UsageInputColumn>
                  <FieldLabelWithInfo>
                    <FieldLabel>이용횟수<RequiredMark>*</RequiredMark></FieldLabel>
                    <Tooltip
                      title={
                        <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>레슨을 받을 수 있는 총 횟수를 설정합니다.</Box>
                      }
                      arrow
                      placement="top">
                      <InfoIconSmall>
                        <InfoCircleIcon />
                      </InfoIconSmall>
                    </Tooltip>
                  </FieldLabelWithInfo>
                  <UsageCountInputBox hasError={errorFields.usageCount}>
                    <FormInputText className="usage-count-input" control={control} name="usageCount" placeholder="0" />
                    <InputUnit>회</InputUnit>
                  </UsageCountInputBox>
                </UsageInputColumn>
                <UsageInputColumn>
                  <FieldLabelWithInfo>
                    <FieldLabel>수업시간<RequiredMark>*</RequiredMark></FieldLabel>
                    <Tooltip
                      title={<Box sx={{ fontSize: 12, lineHeight: 1.5 }}>1회 레슨의 진행 시간(분)을 설정합니다.</Box>}
                      arrow
                      placement="top">
                      <InfoIconSmall>
                        <InfoCircleIcon />
                      </InfoIconSmall>
                    </Tooltip>
                  </FieldLabelWithInfo>
                  <UsageCountInputBox hasError={errorFields.classDefaultMinute}>
                    <FormInputText
                      className="usage-count-input"
                      control={control}
                      name="classDefaultMinute"
                      placeholder="0"
                    />
                    <InputUnit>분</InputUnit>
                  </UsageCountInputBox>
                </UsageInputColumn>
              </UsageInputRow>
            </FieldGroup>
          )}

          {/* 중지권 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>중지권<RequiredMark>*</RequiredMark></FieldLabel>
                <Tooltip
                  title={
                    <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                      무제한: 중지 횟수·기간 제한 없음
                      <br />
                      중지 불가: 회원이 중지 신청 불가
                      <br />
                      직접 입력: 허용할 중지 횟수와 기간 지정
                    </Box>
                  }
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <InfoCircleIcon />
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
            </FieldLabelRow>
            <RadioGroupRow>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'UNLIMITED');
                  setValue('stopCount', '');
                  setValue('stopPeriod', '');
                }}>
                <RadioCircleNew checked={watch('stopType') === 'UNLIMITED'}>
                  {watch('stopType') === 'UNLIMITED' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>무제한</RadioLabelNew>
              </RadioOption>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'DISABLED');
                  setValue('stopCount', 0);
                  setValue('stopPeriod', 0);
                }}>
                <RadioCircleNew checked={watch('stopType') === 'DISABLED'}>
                  {watch('stopType') === 'DISABLED' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>중지 불가</RadioLabelNew>
              </RadioOption>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'CUSTOM');
                  if (!watch('stopCount')) setValue('stopCount', 1);
                  if (!watch('stopPeriod')) setValue('stopPeriod', 30);
                }}>
                <RadioCircleNew checked={watch('stopType') === 'CUSTOM'}>
                  {watch('stopType') === 'CUSTOM' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>직접 입력</RadioLabelNew>
              </RadioOption>
            </RadioGroupRow>
            <HalfWidthContainer>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="stopCount"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('stopType') !== 'CUSTOM',
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="stopPeriod"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('stopType') !== 'CUSTOM',
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>일</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
            </HalfWidthContainer>
          </FieldGroup>

          {/* 이용제한 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>이용제한<RequiredMark>*</RequiredMark></FieldLabel>
                <Tooltip
                  title={<Box sx={{ fontSize: 12, lineHeight: 1.5 }}>일일, 주간별 최대 이용 횟수를 설정합니다.</Box>}
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <InfoCircleIcon />
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('dailyUsageLimits') && watch('weeklyUsageLimits')}
                  onClick={() => {
                    const newValue = !(watch('dailyUsageLimits') && watch('weeklyUsageLimits'));
                    setValue('dailyUsageLimits', newValue);
                    setValue('weeklyUsageLimits', newValue);
                    if (newValue) {
                      setValue('dailyUsageLimit', '');
                      setValue('weeklyUsageLimit', '');
                    }
                  }}>
                  {watch('dailyUsageLimits') && watch('weeklyUsageLimits') && <CheckIcon />}
                </CheckboxNew>
                <CheckboxLabelNew>무제한</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <HalfWidthContainer>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="dailyUsageLimit"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('dailyUsageLimits'),
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>일일/회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="weeklyUsageLimit"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('weeklyUsageLimits'),
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>주간/회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
            </HalfWidthContainer>
          </FieldGroup>

          {/* 레슨일 때: 최대 예약권 */}
          {isLesson && (
            <FieldGroup>
              <FieldLabelRow>
                <FieldLabelWithInfo>
                  <FieldLabel>최대 예약권<RequiredMark>*</RequiredMark></FieldLabel>
                  <Tooltip
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>동시에 예약할 수 있는 최대 횟수를 설정합니다.</Box>
                    }
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <InfoCircleIcon />
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <CheckboxRow>
                  <CheckboxNew
                    checked={watch('reservationCounts')}
                    onClick={() => {
                      const newValue = !watch('reservationCounts');
                      setValue('reservationCounts', newValue);
                      if (newValue) setValue('reservationCount', '');
                    }}>
                    {watch('reservationCounts') && <CheckIcon />}
                  </CheckboxNew>
                  <CheckboxLabelNew>무제한</CheckboxLabelNew>
                </CheckboxRow>
              </FieldLabelRow>
              <FormInputText
                control={control}
                name="reservationCount"
                placeholder="0"
                InputProps={{
                  readOnly: watch('reservationCounts'),
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </FieldGroup>
          )}

          {/* 레슨일 때: 예약 취소권 */}
          {isLesson && (
            <FieldGroup>
              <FieldLabelRow>
                <FieldLabelWithInfo>
                  <FieldLabel>예약 취소권<RequiredMark>*</RequiredMark></FieldLabel>
                  <Tooltip
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>예약을 취소할 수 있는 최대 횟수를 설정합니다.</Box>
                    }
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <InfoCircleIcon />
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <CheckboxRow>
                  <CheckboxNew
                    checked={watch('cancelCounts')}
                    onClick={() => {
                      const newValue = !watch('cancelCounts');
                      setValue('cancelCounts', newValue);
                      if (newValue) setValue('cancelCount', '');
                    }}>
                    {watch('cancelCounts') && <CheckIcon />}
                  </CheckboxNew>
                  <CheckboxLabelNew>무제한</CheckboxLabelNew>
                </CheckboxRow>
              </FieldLabelRow>
              <FormInputText
                control={control}
                name="cancelCount"
                placeholder="0"
                InputProps={{
                  readOnly: watch('cancelCounts'),
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </FieldGroup>
          )}

        </InfoCard>
        )}

        {/* Card 3: 금액설정 + 구매설정 (레슨: 세로 묶음 / 이용권: 각각 독립 컬럼) */}
        <Box
          sx={
            isLesson
              ? { display: 'flex', flexDirection: 'column', gap: '24px', flex: 1, minWidth: 0 }
              : { display: 'contents' }
          }>
          {/* 금액설정 */}
          <InfoCard sx={{ width: '100%' }}>
            <InfoCardTitle>금액설정</InfoCardTitle>

            {/* 판매금액 */}
            <FieldGroup>
              <FieldLabel>판매금액<RequiredMark>*</RequiredMark></FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.regularPrice} fullWidth>
                <FormInputPrice
                  control={control}
                  rules={{ required: true }}
                  name="regularPrice"
                  placeholder="0"
                  type="tel"
                  onChange={handleRegularPriceChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>원</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </ErrorFieldWrapper>
            </FieldGroup>

            {/* 할인금액 */}
            <FieldGroup>
              <FieldLabel>할인금액</FieldLabel>
              <FormInputPrice
                control={control}
                name="discountRate"
                placeholder="0"
                onChange={handleDiscountRateChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <DiscountUnitSelect onClick={e => setDiscountUnitAnchor(e.currentTarget)}>
                        <span className="unit-text">{discountType}</span>
                        <span className="unit-arrow">
                          <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                        </span>
                      </DiscountUnitSelect>
                      <Menu
                        anchorEl={discountUnitAnchor}
                        open={Boolean(discountUnitAnchor)}
                        onClose={() => setDiscountUnitAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <MenuItem
                          onClick={() => {
                            setDiscountType('원');
                            setDiscountUnitAnchor(null);
                          }}>
                          원
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setDiscountType('%');
                            setDiscountUnitAnchor(null);
                          }}>
                          %
                        </MenuItem>
                      </Menu>
                    </InputAdornment>
                  ),
                }}
              />
            </FieldGroup>

            {/* 결제금액 */}
            <FieldGroup>
              <FieldLabel>결제금액</FieldLabel>
              <FormInputPrice
                control={control}
                name="finalPrice"
                placeholder="0"
                onChange={handleFinalPriceChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>원</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </FieldGroup>
          </InfoCard>

          {/* 구매설정 */}
          <InfoCard sx={{ width: '100%' }}>
            <InfoCardTitle>구매설정</InfoCardTitle>

            {/* 총 수량 */}
            <FieldGroup>
              <FieldLabelRow>
                <FieldLabelWithInfo>
                  <FieldLabel>총 수량<RequiredMark>*</RequiredMark></FieldLabel>
                  <Tooltip
                    title={<Box sx={{ fontSize: 12, lineHeight: 1.5 }}>판매할 일일권의 총 수량을 설정합니다.</Box>}
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <InfoCircleIcon />
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <CheckboxRow>
                  <CheckboxNew
                    checked={watch('totalQuantityUnlimited')}
                    onClick={() => {
                      const newValue = !watch('totalQuantityUnlimited');
                      setValue('totalQuantityUnlimited', newValue);
                      if (newValue) setValue('totalQuantity', '');
                    }}>
                    {watch('totalQuantityUnlimited') && <CheckIcon />}
                  </CheckboxNew>
                  <CheckboxLabelNew>무제한</CheckboxLabelNew>
                </CheckboxRow>
              </FieldLabelRow>
              <FormInputText
                control={control}
                name="totalQuantity"
                placeholder="0"
                InputProps={{
                  readOnly: watch('totalQuantityUnlimited'),
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </FieldGroup>

            {/* 인당 최대 구매 횟수 */}
            <FieldGroup>
              <FieldLabelRow>
                <FieldLabelWithInfo>
                  <FieldLabel>인당 최대 구매 횟수<RequiredMark>*</RequiredMark></FieldLabel>
                  <Tooltip
                    title={<Box sx={{ fontSize: 12, lineHeight: 1.5 }}>1인당 구매 가능한 최대 횟수를 설정합니다.</Box>}
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <InfoCircleIcon />
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <CheckboxRow>
                  <CheckboxNew
                    checked={watch('maxTrialPerUserUnlimited')}
                    onClick={() => {
                      const newValue = !watch('maxTrialPerUserUnlimited');
                      setValue('maxTrialPerUserUnlimited', newValue);
                      if (newValue) setValue('maxTrialPerUser', '');
                    }}>
                    {watch('maxTrialPerUserUnlimited') && <CheckIcon />}
                  </CheckboxNew>
                  <CheckboxLabelNew>무제한</CheckboxLabelNew>
                </CheckboxRow>
              </FieldLabelRow>
              <FormInputText
                control={control}
                name="maxTrialPerUser"
                placeholder="0"
                InputProps={{
                  readOnly: watch('maxTrialPerUserUnlimited'),
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </FieldGroup>
          </InfoCard>
        </Box>

        {/* Card 4: 기간설정 */}
        <InfoCard>
          <InfoCardTitle>기간설정</InfoCardTitle>

          {/* 판매기간 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>판매기간<RequiredMark>*</RequiredMark></FieldLabel>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('hasSaleEndDate')}
                  onClick={() => setValue('hasSaleEndDate', !watch('hasSaleEndDate'))}>
                  {watch('hasSaleEndDate') && <CheckIcon />}
                </CheckboxNew>
                <CheckboxLabelNew>종료일 없음</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <DatePickerRow>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="saleStartDate"
                    control={control}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
              <TimeTilde>~</TimeTilde>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="saleEndDate"
                    control={control}
                    disabled={watch('hasSaleEndDate')}
                    minDate={watch('saleStartDate')}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
            </DatePickerRow>
          </FieldGroup>

          {/* 할인기간 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>할인기간<RequiredMark>*</RequiredMark></FieldLabel>
              <CheckboxRow>
                <CheckboxNew checked={hasDiscountEndDate} onClick={() => setHasDiscountEndDate(!hasDiscountEndDate)}>
                  {hasDiscountEndDate && <CheckIcon />}
                </CheckboxNew>
                <CheckboxLabelNew>종료일 없음</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <DatePickerRow>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="discountStartDate"
                    control={control}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
              <TimeTilde>~</TimeTilde>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="discountEndDate"
                    control={control}
                    disabled={hasDiscountEndDate}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
            </DatePickerRow>
          </FieldGroup>
        </InfoCard>
      </CardsContainer>

      {/* Bottom Buttons */}
      <BottomActionsNew>
        <CancelButtonNew onClick={onCancel}>취소</CancelButtonNew>
        <NextButtonNew onClick={handleNext} disabled={loader}>
          다음
        </NextButtonNew>
      </BottomActionsNew>
    </>
  );
}

export default TrialEditStep01;
